/**
 * Axios 등 API 호출 실패를 구독해 전역 배너(백엔드/프록시 단절 등)에 반영한다.
 * React와 순환 참조를 피하기 위해 Http.js에서만 이 모듈을 호출한다.
 */

const listeners = new Set();

const initialState = {
  degraded: false,
  /** @type {'network'|'gateway'|'timeout'|'unknown'|null} */
  reason: null,
  status: null,
  at: null,
  messageKey: null,
};

let state = { ...initialState };

export function getApiHealthState() {
  return { ...state };
}

export function subscribeApiHealth(listener) {
  listeners.add(listener);
  listener(getApiHealthState());
  return () => {
    listeners.delete(listener);
  };
}

function emit() {
  const snap = getApiHealthState();
  listeners.forEach((fn) => {
    try {
      fn(snap);
    } catch (e) {
      console.error('[apiHealth] listener error', e);
    }
  });
}

/**
 * 네트워크 단절, 게이트웨이(502/503/504), 개발 프록시 오류(Proxy error) 등
 * "백엔드에 연결되지 않는" 상황인지 판별한다.
 * 단일 API의 500(앱 오류)은 포함하지 않는다.
 */
export function isBackendConnectivityFailure(error) {
  if (!error) return false;

  if (error.code === 'ERR_NETWORK') return true;

  if (!error.response) {
    return true;
  }

  const status = error.response.status;
  if (status === 502 || status === 503 || status === 504) return true;

  const data = error.response.data;
  if (status === 500 && data && typeof data === 'object') {
    if (data.error === 'Proxy error') return true;
    const errStr = String(data.error || data.message || '').toLowerCase();
    if (errStr.includes('proxy') && errStr.includes('error')) return true;
  }

  return false;
}

function classifyConnectivityFailure(error) {
  if (error?.code === 'ECONNABORTED') {
    return { reason: 'timeout', messageKey: 'apiHealth.message.timeout' };
  }
  if (error?.code === 'ERR_NETWORK' || !error?.response) {
    return { reason: 'network', messageKey: 'apiHealth.message.network' };
  }
  const s = error.response.status;
  if (s === 502 || s === 503 || s === 504) {
    return { reason: 'gateway', messageKey: 'apiHealth.message.gateway' };
  }
  const data = error.response.data;
  if (s === 500 && data && typeof data === 'object' && data.error === 'Proxy error') {
    return { reason: 'gateway', messageKey: 'apiHealth.message.proxy500' };
  }
  return { reason: 'unknown', messageKey: 'apiHealth.message.unknown' };
}

export function reportApiConnectivityFailure(error) {
  if (!isBackendConnectivityFailure(error)) return;
  const { reason, messageKey } = classifyConnectivityFailure(error);
  const status = error?.response?.status ?? null;
  state = {
    degraded: true,
    reason,
    status,
    at: Date.now(),
    messageKey,
  };
  emit();
}

/** 2xx 응답 시 연결 정상으로 간주하고 배너를 내린다. */
export function reportApiConnectivityOk() {
  if (!state.degraded) return;
  state = { ...initialState };
  emit();
}
